import { useEffect, useState } from 'react';
import {
  DataForAdvisor,
  ProcessedStudentsForAdvisor,
  SortDirection,
  SortParameter
} from "../../../api/client";
import useColumns from "./columns";
import { DataTable } from "./DataTable";
import { DataTableProcessed } from "./DataTableProcessed";
import { AuthContext } from "../../../components/AuthWrapper";
import { useOutletContext } from "react-router-dom";
import { columnsProcessed } from "./columnsProcessed";
import toast from 'react-hot-toast';
import { determineWebSocketEnvironment } from './Helpers';
import { Heading } from '../../../components/UiComponents/heading';
import PageTitle from '../../../components/PageTitle';
import { Container } from '../../../components/Container';
import ConfirmationModal from '../../../components/ConfirmModal';
import { Button } from '../../../components/UiComponents/button';
import { Link45deg } from 'react-bootstrap-icons';
import { GetLocalFrontendBaseUrl } from '../../../helpers/stringHelpers';


let [url, urlNewPage] = determineWebSocketEnvironment();

interface WaitingRoomProps {
  currentLine: DataForAdvisor[]
  fetchWaitingStudents: () => void
  setLastSeenWaitingLine: (line: DataForAdvisor[]) => void
}

const WaitingRoom = ({currentLine, fetchWaitingStudents, setLastSeenWaitingLine}: WaitingRoomProps): JSX.Element => {
  const [showCurrentLine, setShowCurrentLine] = useState<boolean>(true);
  const [processedStudents, setProcessedStudents] = useState<ProcessedStudentsForAdvisor[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const { client, state }: AuthContext = useOutletContext();

  // create 4 useStates one for pageIndex, two for direction and one for parameters
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [directionForJoinedQueue, setDirectionForJoinedQueue] = useState<SortDirection | null>(null);
  const [directionForProcessed, setDirectionForProcessed] = useState<SortDirection | null>(null);
  const [parameters, setParameters] = useState<SortParameter | null>(null);
  let response: ProcessedStudentsForAdvisor[] = [];

  const [isConfirmationModelOpen, setConfirmationModelOpen] = useState<boolean>(false);
  const [appointmentIdToDelete, setAppointmentIdToDelete] = useState<string>('');

  const fetchProcessedStudents = async (pageIndex: number) => {
    setIsLoading(true);

    try {
      let result;
      if (parameters === SortParameter.JoinedQueue && directionForJoinedQueue) {
        result = await client?.admin_GetProcessedStudents(pageIndex, pageSize, parameters, directionForJoinedQueue);
      } else if (parameters === SortParameter.ProcessedTime && directionForProcessed) {
        result = await client?.admin_GetProcessedStudents(pageIndex, pageSize, parameters, directionForProcessed);
      } else {
        result = await client?.admin_GetProcessedStudents(pageIndex, pageSize, null, null);
      }

      // Ensure proper response handling
      response = result?.results ?? [];
      setProcessedStudents(response);
      setTotalRows(result?.count ?? 0);

    } catch (error) {
      toast.error(`Failed to fetch processed students: ${error}`);
      setProcessedStudents([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProcessedStudents(pageIndex);
  }, [pageIndex, directionForJoinedQueue, directionForProcessed, parameters]);

  useEffect(() => {
    setLastSeenWaitingLine(currentLine);
  }, [currentLine])


  const removeFromLine = async (appointmentId: string) => {
    try {
      await client?.admin_CancelAppointment(appointmentId, state.user?.id)
      toast.success("Successfully canceled appointment.")
    }
    catch (error) {
      toast.error(`Failed to cancel the specified appointment: ${error}`)
    }
    finally {
      fetchWaitingStudents();
    }
  };


  const processStudent = async (appointmentId: string, studentId: string | undefined) => {
      try {
        await client?.admin_ProcessAppointment(appointmentId, state.user?.id)
        toast.success("Student processed successfully");

        // Open the new page only if no error occurred
        if (studentId) {
          window.open(urlNewPage + studentId);
        } else {
          toast.error("Cannot open page: studentId is undefined");
        }
      } catch (error) {
        toast.error(`Error processing student: ${error}`);
      }
  };

  const openTheConfirmationModel = (idToDelete: string) => {
    setConfirmationModelOpen(true);
    setAppointmentIdToDelete(idToDelete);
  }

  const columns = useColumns({ onRemove: openTheConfirmationModel, onProcess: processStudent });

  return (
    <>
      <Container>
        <PageTitle title="Waiting Room" />
        <Heading text="Waiting Room" />
          <Button
          variant="default"
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(`${GetLocalFrontendBaseUrl()}/checkin`);
            toast.success("Copied link to clipboard!")
          }}
          >
          <Link45deg className='w-6 h-6 mr-1'/>
          <span>Copy Student Check-In Link</span>
          </Button>
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-lg p-1 bg-gray-200 dark:bg-slate-600 dark:text-white">
            {/* The classNames of the Buttons below should not be deleted because they have special styling for special appearances; these classNames are NOT redundant */}
            <Button
              variant="ghost"
              className={`px-4 py-2 rounded-md transition-all duration-200 ease-in-out ${showCurrentLine
                ? 'bg-white dark:bg-slate-400 text-black shadow-md transform scale-105'
                : 'text-gray-600 dark:text-gray-300'
                }`}
              onClick={() => setShowCurrentLine(true)}
              abbr="Click to view students still in waiting line"
            >
              Current Line
            </Button>
            <Button
              variant="ghost"
              className={`px-4 py-2 rounded-md transition-all duration-200 ease-in-out ${!showCurrentLine
                ? 'bg-white dark:bg-slate-400 text-black shadow-md transform scale-105'
                : 'text-gray-600 dark:text-gray-300'
                }`}
              onClick={() => setShowCurrentLine(false)}
              abbr="Click to view students who have been processed"
            >
              Processed Students
            </Button>
          </div>
        </div>

        <div >
          {showCurrentLine ? (
            <DataTable columns={columns} data={currentLine} />
          ) : (
            <DataTableProcessed
              columns={columnsProcessed(setDirectionForJoinedQueue, setDirectionForProcessed, setParameters)}
              data={processedStudents}
              pageCount={Math.max(Math.ceil(totalRows / pageSize), 1)}
              fetchData={fetchProcessedStudents}
              isLoading={isLoading}
            />
          )}
        </div>
      </Container>
      <ConfirmationModal
        isOpen={isConfirmationModelOpen}
        onClose={() => setConfirmationModelOpen(false)}
        onConfirm={() => removeFromLine(appointmentIdToDelete)}
        Id={appointmentIdToDelete || ''}
        TextMessage="Are you sure the student was added to the line by accident?"
        abbrCancel="No, keep student in waiting line"
        abbrConfirm="Yes, remove student"
      />
    </>
  );
};

export default WaitingRoom;
